import {
  Decision,
  DecisionReason,
  FeatureFlagDecision,
  HackleEvent,
  HackleRemoteConfig,
  Properties,
  sanitizeUser,
  User,
  VariationKey
} from "../../core/internal/model/model"
import { HackleExperiment, HackleInAppMessageListener, HackleInAppMessageView } from "../../public/model"
import PropertyUtil from "../../core/internal/util/PropertyUtil"
import IdentifierUtil from "../../core/internal/util/IdentifierUtil"
import { PropertyOperations, PropertyOperationsBuilder } from "../property/PropertyOperations"
import { BrowserHackleClient, PageView } from "../index.browser"
import HackleWebAppRemoteConfig from "./HackleWebAppRemoteConfig"
import { DefaultParameterConfig } from "../../core/internal/config/ParameterConfig"
import Logger from "../../core/internal/logger"
import {
  FeatureFlagDetailInvocation,
  FeatureFlagInvocationDto,
  GetSessionIdInvocation,
  GetUserInvocation,
  HackleAppInvocationProcessor,
  InvocationDecisionDto,
  InvocationFeatureFlagDecisionDto,
  IsFeatureOnInvocation,
  ResetUserInvocation,
  SetDeviceIdInvocation,
  SetPhoneNumberInvocation,
  SetUserIdInvocation,
  SetUserInvocation,
  SetUserPropertyInvocation,
  ShowUserExplorerInvocation,
  HideUserExplorerInvocation,
  TrackInvocation,
  UnsetPhoneNumberInvocation,
  UpdateUserPropertiesInvocation,
  VariationDetailInvocation,
  VariationInvocation,
  VariationInvocationDto,
  UpdatePushSubscriptionsInvocation,
  UpdateSmsSubscriptionsInvocation,
  UpdateKakaoSubscriptionsInvocation
} from "./invocator/HackleAppInvocationProcessor"
import { getUserId } from "../user/index.browser"
import { HackleSubscriptionOperations } from "../subscription/HackleSubscriptionOperations"
import ObjectUtil from "../../core/internal/util/ObjectUtil"
import { BrowserPropertyProvider } from "../property/BrowserPropertyProvider"

const log = Logger.log

export interface HackleApp {
  getAppSdkKey: () => string
  getAppMode?: () => string
  getInvocationType: () => string
  invoke?: (stringified: string) => string | null
  getWebViewConfig?: () => string
}

declare global {
  interface Window {
    _hackleApp?: HackleApp
  }
}

export enum HackleAppMode {
  native = "native",
  web_view_wrapper = "web_view_wrapper"
}

export function isHackleWebApp(_window: Window): _window is Window & {
  _hackleApp: HackleApp
} {
  if (!_window || !("_hackleApp" in _window) || _window._hackleApp === null || typeof _window._hackleApp !== "object") {
    return false
  }

  if (!("getAppSdkKey" in _window._hackleApp) || typeof _window._hackleApp.getAppSdkKey !== "function") {
    return false
  }

  if (!("getInvocationType" in _window._hackleApp) || typeof _window._hackleApp.getInvocationType !== "function") {
    return false
  }

  return true
}

export class NativeAppHackleClient implements BrowserHackleClient {
  constructor(
    private readonly invocationProcessor: HackleAppInvocationProcessor,
    private readonly browserPropertyProvider: BrowserPropertyProvider
  ) {
    // This is to prevent the "This Binding" problem.
    this.getSessionId = this.getSessionId.bind(this)
    this.getUser = this.getUser.bind(this)
    this.setUser = this.setUser.bind(this)
    this.setUserId = this.setUserId.bind(this)
    this.setDeviceId = this.setDeviceId.bind(this)
    this.setUserProperty = this.setUserProperty.bind(this)
    this.setUserProperties = this.setUserProperties.bind(this)
    this.updateUserProperties = this.updateUserProperties.bind(this)
    this.updatePushSubscriptions = this.updatePushSubscriptions.bind(this)
    this.updateSmsSubscriptions = this.updateSmsSubscriptions.bind(this)
    this.updateKakaoSubscriptions = this.updateKakaoSubscriptions.bind(this)
    this.resetUser = this.resetUser.bind(this)
    this.variation = this.variation.bind(this)
    this.variationDetail = this.variationDetail.bind(this)
    this.isFeatureOn = this.isFeatureOn.bind(this)
    this.featureFlagDetail = this.featureFlagDetail.bind(this)
    this.track = this.track.bind(this)
    this.trackPageView = this.trackPageView.bind(this)
    this.remoteConfig = this.remoteConfig.bind(this)
    this.onReady = this.onReady.bind(this)
    this.onInitialized = this.onInitialized.bind(this)
    this.showUserExplorer = this.showUserExplorer.bind(this)
    this.hideUserExplorer = this.hideUserExplorer.bind(this)
    this.fetch = this.fetch.bind(this)
    this.close = this.close.bind(this)
    this.setInAppMessageListener = this.setInAppMessageListener.bind(this)
    this.getExperiment = this.getExperiment.bind(this)
    this.setPhoneNumber = this.setPhoneNumber.bind(this)
    this.unsetPhoneNumber = this.unsetPhoneNumber.bind(this)
    this.getDisplayedInAppMessageView = this.getDisplayedInAppMessageView.bind(this)
  }

  getSessionId(): string {
    try {
      const getSessionIdInvocation: GetSessionIdInvocation = {
        command: "getSessionId",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      const result = this.invocationProcessor.process<never, string>(getSessionIdInvocation)
      return result ?? getUserId()
    } catch (e) {
      log.error(`Unexpected exception while get sessionId: ${e}`)
      return getUserId()
    }
  }

  getUser(): User {
    try {
      const getUserInvocation: GetUserInvocation = {
        command: "getUser",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      const result = this.invocationProcessor.process<never, User>(getUserInvocation)
      return sanitizeUser(result) ?? this.defaultUser()
    } catch (e) {
      log.error(`Unexpected exception while get user: ${e}`)
      return this.defaultUser()
    }
  }

  async setUser(user: User): Promise<void> {
    try {
      const sanitizedUser = sanitizeUser(user)
      if (!sanitizedUser) {
        log.warn("invalid user")
        return
      }

      const setUserInvocation: SetUserInvocation = {
        command: "setUser",
        parameters: { user: sanitizedUser },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(setUserInvocation)
    } catch (e) {
      log.error(`Unexpected exception while set user: ${e}`)
    }
  }

  async setUserId(userId: string | number | undefined | null): Promise<void> {
    try {
      let nextUserId: string | null

      // undefined value is ignored when JSON.stringify(), so we need to convert it to null
      if (ObjectUtil.isNullOrUndefined(userId)) {
        nextUserId = null
      } else {
        nextUserId = IdentifierUtil.sanitizeValue(userId)
        if (!nextUserId) {
          log.warn(`Invalid userId. [userId=${userId}]`)
          return
        }
      }
      const setUserIdInvocation: SetUserIdInvocation = {
        command: "setUserId",
        parameters: { userId: nextUserId },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(setUserIdInvocation)
    } catch (e) {
      log.error(`Unexpected exception while set userId: ${e}`)
    }
  }

  async setDeviceId(deviceId: string): Promise<void> {
    try {
      const sanitizedDeviceId = IdentifierUtil.sanitizeValue(deviceId)
      if (!sanitizedDeviceId) {
        log.warn(`Invalid deviceId. [deviceId=${deviceId}]`)
        return
      }

      const setDeviceIdInvocation: SetDeviceIdInvocation = {
        command: "setDeviceId",
        parameters: { deviceId: sanitizedDeviceId },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(setDeviceIdInvocation)
    } catch (e) {
      log.error(`Unexpected exception while set deviceId: ${e}`)
    }
  }

  async setUserProperty(key: string, value: any): Promise<void> {
    try {
      const setUserPropertyInvocation: SetUserPropertyInvocation = {
        command: "setUserProperty",
        parameters: { key, value },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(setUserPropertyInvocation)
    } catch (e) {
      log.error(`Unexpected exception while set userProperty: ${e}`)
    }
  }

  async setUserProperties(properties: Properties<string>): Promise<void> {
    try {
      const sanitizedProperties = PropertyUtil.sanitize(properties)
      const operations = new PropertyOperationsBuilder()

      Object.entries(sanitizedProperties).forEach(([key, value]) => {
        operations.set(key, value)
      })
      this.updateUserProperties(operations.build())
    } catch (e) {
      log.error(`Unexpected exception while set userProperties: ${e}`)
    }
  }

  async updateUserProperties(operations: PropertyOperations): Promise<void> {
    try {
      const updateUserPropertiesInvocation: UpdateUserPropertiesInvocation = {
        command: "updateUserProperties",
        parameters: { operations: operations.toRecord() },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(updateUserPropertiesInvocation)
    } catch (e) {
      log.error(`Unexpected exception while update userProperties: ${e}`)
    }
  }

  async updatePushSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    try {
      const updatePushSubscriptionsInvocation: UpdatePushSubscriptionsInvocation = {
        command: "updatePushSubscriptions",
        parameters: { operations: operations.toRecord() },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(updatePushSubscriptionsInvocation)
    } catch (e) {
      log.error(`Unexpected exception while update push subscriptions: ${e}`)
    }
  }

  async updateSmsSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    try {
      const updateSmsSubscriptionsInvocation: UpdateSmsSubscriptionsInvocation = {
        command: "updateSmsSubscriptions",
        parameters: { operations: operations.toRecord() },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(updateSmsSubscriptionsInvocation)
    } catch (e) {
      log.error(`Unexpected exception while update sms subscriptions: ${e}`)
    }
  }

  async updateKakaoSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    try {
      const updateKakaoSubscriptionsInvocation: UpdateKakaoSubscriptionsInvocation = {
        command: "updateKakaoSubscriptions",
        parameters: { operations: operations.toRecord() },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(updateKakaoSubscriptionsInvocation)
    } catch (e) {
      log.error(`Unexpected exception while update Kakao subscriptions: ${e}`)
    }
  }

  async resetUser(): Promise<void> {
    try {
      const resetUserInvocation: ResetUserInvocation = {
        command: "resetUser",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(resetUserInvocation)
    } catch (e) {
      log.error(`Unexpected exception while reset user: ${e}`)
    }
  }

  async setPhoneNumber(phoneNumber: string): Promise<void> {
    try {
      if (typeof phoneNumber !== "string") {
        log.error(`Invalid type ${phoneNumber}: phoneNumber value must be string.`)
        return Promise.resolve()
      }

      const invocation: SetPhoneNumberInvocation = {
        command: "setPhoneNumber",
        parameters: { phoneNumber },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(invocation)
    } catch (e) {
      log.error(`Unexpected exception occurred while setting phone number: ${e}`)
    }
  }

  async unsetPhoneNumber(): Promise<void> {
    try {
      const invocation: UnsetPhoneNumberInvocation = {
        command: "unsetPhoneNumber",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(invocation)
    } catch (e) {
      log.error(`Unexpected exception occurred while unsetting phone number: ${e}`)
    }
  }

  variation(experimentKey: number, user?: User | string, defaultVariation: VariationKey = "A"): string {
    try {
      const variationInvocation: VariationInvocation = {
        command: "variation",
        parameters: { experimentKey, defaultVariation, user: this.convertUser(user) },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }

      const result = this.invocationProcessor.process<VariationInvocationDto, string>(variationInvocation)

      if (!result) {
        throw new Error("invoke result data not exists")
      }

      return result
    } catch (e) {
      log.error(
        `Unexpected exception while deciding variation for experiment[${experimentKey}]. Returning default variation[${defaultVariation}] : ${e}`
      )
      return defaultVariation
    }
  }

  variationDetail(experimentKey: number, user?: User | string, defaultVariation: VariationKey = "A"): Decision {
    try {
      const variationDetailInvocation: VariationDetailInvocation = {
        command: "variationDetail",
        parameters: { experimentKey, defaultVariation, user: this.convertUser(user) },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }

      const result = this.invocationProcessor.process<VariationInvocationDto, InvocationDecisionDto>(
        variationDetailInvocation
      )

      if (result === null) {
        throw new Error("invoke result data not exists")
      }

      return Decision.of(result.variation, result.reason, new DefaultParameterConfig(result.config.parameters ?? {}))
    } catch (e) {
      log.error(
        `Unexpected exception while deciding variation for experiment[${experimentKey}]. Returning default variation[${defaultVariation}] : ${e}`
      )
      return Decision.of(defaultVariation, DecisionReason.EXCEPTION)
    }
  }

  isFeatureOn(featureKey: number, user?: User | string): boolean {
    try {
      const isFeatureOnInvocation: IsFeatureOnInvocation = {
        command: "isFeatureOn",
        parameters: { featureKey, user: this.convertUser(user) },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }

      const result = this.invocationProcessor.process<FeatureFlagInvocationDto, boolean>(isFeatureOnInvocation)

      if (result === null) {
        throw new Error("invoke result data not exists")
      }

      return Boolean(result)
    } catch (e) {
      log.error(
        `Unexpected exception while deciding feature flag[${featureKey}]. Returning default value[false] : ${e}`
      )
      return false
    }
  }

  featureFlagDetail(featureKey: number, user?: User | string): FeatureFlagDecision {
    try {
      const featureFlagDetailInvocation: FeatureFlagDetailInvocation = {
        command: "featureFlagDetail",
        parameters: { featureKey, user: this.convertUser(user) },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }

      const result = this.invocationProcessor.process<FeatureFlagInvocationDto, InvocationFeatureFlagDecisionDto>(
        featureFlagDetailInvocation
      )

      if (result === null) {
        throw new Error("invoke result data not exists")
      }

      return new FeatureFlagDecision(
        result.isOn,
        result.reason,
        new DefaultParameterConfig(result.config.parameters ?? {}),
        undefined
      )
    } catch (e) {
      log.error(
        `Unexpected exception while deciding feature flag[${featureKey}]. Returning default value[false] : ${e}`
      )
      return FeatureFlagDecision.off(DecisionReason.EXCEPTION)
    }
  }

  track(event: HackleEvent | string, user?: User | string) {
    try {
      const trackInvocation: TrackInvocation = {
        command: "track",
        parameters: { event: this.convertEvent(event), user: this.convertUser(user) },
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }

      this.invocationProcessor.process(trackInvocation)
    } catch (e) {
      log.error(`Unexpected exception while tracking event: ${e}`)
    }
  }

  trackPageView(option?: PageView | undefined): void {
    log.info(`Web to app integration feature is not support 'trackPageView' method`)
  }

  remoteConfig(user?: string | User | undefined): HackleRemoteConfig {
    return new HackleWebAppRemoteConfig(this.invocationProcessor, this.convertUser(user), this.browserPropertyProvider)
  }

  onReady(block: () => void, timeout?: number | undefined): void {
    block()
  }

  showUserExplorer(): void {
    try {
      const showUserExplorerInvocation: ShowUserExplorerInvocation = {
        command: "showUserExplorer",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(showUserExplorerInvocation)
    } catch (e) {
      log.error(`Unexpected exception while show userExplorer: ${e}`)
    }
  }

  hideUserExplorer(): void {
    try {
      const hideUserExplorerInvocation: HideUserExplorerInvocation = {
        command: "hideUserExplorer",
        browserProperties: this.browserPropertyProvider.getBrowserProperties()
      }
      this.invocationProcessor.process(hideUserExplorerInvocation)
    } catch (e) {
      log.error(`Unexpected exception while hide userExplorer: ${e}`)
    }
  }

  setInAppMessageListener(_listener: HackleInAppMessageListener | null): void {
    log.info(`Web to app integration feature is not support 'setInAppMessageListener' method`)
  }

  fetch(): Promise<void> {
    return Promise.resolve()
  }

  onInitialized(config?: { timeout?: number | undefined } | undefined): Promise<{ success: boolean }> {
    return Promise.resolve({ success: true })
  }

  close(): void {
    log.info(`Web to app integration feature is not support 'close' method`)
  }

  getExperiment(experimentKey: number): HackleExperiment | undefined {
    log.info(`Web to app integration feature is not support 'getExperiment' method`)
    return undefined
  }

  private convertEvent(event: HackleEvent | string): HackleEvent {
    if (typeof event === "string") {
      return { key: event }
    }
    return event
  }

  private convertUser(user?: User | string): User | string | undefined {
    if (typeof user === "string") return user
    if (!user) return undefined
    return sanitizeUser(user) ?? undefined
  }

  private defaultUser(): User {
    return {
      deviceId: getUserId()
    }
  }

  getDisplayedInAppMessageView(): HackleInAppMessageView | null {
    log.info(`Web to app integration feature is not support 'getDisplayedInAppMessageView' method`)
    return null
  }
}

export class WebViewHackleClient implements BrowserHackleClient {
  constructor(private readonly browser: BrowserHackleClient, private readonly app: BrowserHackleClient) {
    this.getSessionId = this.getSessionId.bind(this)
    this.getUser = this.getUser.bind(this)
    this.setUser = this.setUser.bind(this)
    this.setUserId = this.setUserId.bind(this)
    this.setDeviceId = this.setDeviceId.bind(this)
    this.setUserProperty = this.setUserProperty.bind(this)
    this.setUserProperties = this.setUserProperties.bind(this)
    this.updateUserProperties = this.updateUserProperties.bind(this)
    this.updatePushSubscriptions = this.updatePushSubscriptions.bind(this)
    this.updateSmsSubscriptions = this.updateSmsSubscriptions.bind(this)
    this.updateKakaoSubscriptions = this.updateKakaoSubscriptions.bind(this)
    this.resetUser = this.resetUser.bind(this)
    this.variation = this.variation.bind(this)
    this.variationDetail = this.variationDetail.bind(this)
    this.isFeatureOn = this.isFeatureOn.bind(this)
    this.featureFlagDetail = this.featureFlagDetail.bind(this)
    this.track = this.track.bind(this)
    this.trackPageView = this.trackPageView.bind(this)
    this.remoteConfig = this.remoteConfig.bind(this)
    this.onReady = this.onReady.bind(this)
    this.onInitialized = this.onInitialized.bind(this)
    this.showUserExplorer = this.showUserExplorer.bind(this)
    this.hideUserExplorer = this.hideUserExplorer.bind(this)
    this.fetch = this.fetch.bind(this)
    this.close = this.close.bind(this)
    this.setInAppMessageListener = this.setInAppMessageListener.bind(this)
    this.getExperiment = this.getExperiment.bind(this)
    this.setPhoneNumber = this.setPhoneNumber.bind(this)
    this.unsetPhoneNumber = this.unsetPhoneNumber.bind(this)
    this.getDisplayedInAppMessageView = this.getDisplayedInAppMessageView.bind(this)
  }

  getSessionId(): string {
    return this.browser.getSessionId()
  }

  getUser(): User {
    return this.browser.getUser()
  }

  async syncUser(): Promise<void> {
    const user = this.browser.getUser()
    await this.app.setUser(user)
  }

  async setUser(user: User): Promise<void> {
    await this.browser.setUser(user)
    await this.syncUser()
  }

  async setUserId(userId: string | number | undefined | null): Promise<void> {
    await this.browser.setUserId(userId)
    await this.syncUser()
  }

  async setDeviceId(deviceId: string): Promise<void> {
    await this.browser.setDeviceId(deviceId)
    await this.syncUser()
  }

  async setUserProperty(key: string, value: any): Promise<void> {
    await this.browser.setUserProperty(key, value)
    await this.syncUser()
  }

  async setUserProperties(properties: Properties<string>): Promise<void> {
    await this.browser.setUserProperties(properties)
    await this.syncUser()
  }

  async updateUserProperties(operations: PropertyOperations): Promise<void> {
    await this.browser.updateUserProperties(operations)
    await this.syncUser()
  }

  async updatePushSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    await this.browser.updatePushSubscriptions(operations)
  }

  async updateSmsSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    await this.browser.updateSmsSubscriptions(operations)
  }

  async updateKakaoSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    await this.browser.updateKakaoSubscriptions(operations)
  }

  async resetUser(): Promise<void> {
    await this.browser.resetUser()
    await this.syncUser()
  }

  async setPhoneNumber(phoneNumber: string): Promise<void> {
    await this.browser.setPhoneNumber(phoneNumber)
    await this.syncUser()
  }

  async unsetPhoneNumber(): Promise<void> {
    await this.browser.unsetPhoneNumber()
    await this.syncUser()
  }

  variation(experimentKey: number, user?: User | string, defaultVariation: VariationKey = "A"): string {
    return this.browser.variation(experimentKey, user, defaultVariation)
  }

  variationDetail(experimentKey: number, user?: User | string, defaultVariation: VariationKey = "A"): Decision {
    return this.browser.variationDetail(experimentKey, user, defaultVariation)
  }

  isFeatureOn(featureKey: number, user?: User | string): boolean {
    return this.browser.isFeatureOn(featureKey, user)
  }

  featureFlagDetail(featureKey: number, user?: User | string): FeatureFlagDecision {
    return this.browser.featureFlagDetail(featureKey, user)
  }

  track(event: HackleEvent | string, user?: User | string) {
    return this.browser.track(event, user)
  }

  trackPageView(option?: PageView | undefined): void {
    return this.browser.trackPageView(option)
  }

  remoteConfig(user?: string | User | undefined): HackleRemoteConfig {
    return this.browser.remoteConfig(user)
  }

  onReady(block: () => void, timeout?: number | undefined): void {
    this.browser.onReady(block, timeout)
  }

  showUserExplorer(): void {
    this.browser.showUserExplorer()
  }

  hideUserExplorer(): void {
    this.browser.hideUserExplorer()
  }

  fetch(): Promise<void> {
    return this.browser.fetch()
  }

  onInitialized(config?: { timeout?: number | undefined } | undefined): Promise<{ success: boolean }> {
    return this.browser.onInitialized(config)
  }

  close(): void {
    this.browser.close()
  }

  setInAppMessageListener(listener: HackleInAppMessageListener | null): void {
    this.browser.setInAppMessageListener(listener)
  }

  getExperiment(experimentKey: number): HackleExperiment | undefined {
    return this.browser.getExperiment(experimentKey)
  }

  getDisplayedInAppMessageView(): HackleInAppMessageView | null {
    return this.browser.getDisplayedInAppMessageView()
  }
}
